import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  InputAdornment,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import useApiContext from 'hooks/useApiContext';
import routes from 'router/routes';
import {
  iExhibitorPass,
  iExhibitorPassList,
} from 'services/Ticketing/Ticketing.type';
import {
  PAGE_SX,
  TicketingNav,
  card,
  formatDateTime,
  primaryButton,
  sectionTitle,
  secondaryButton,
} from './common';

/** Badges used / quota, editable in place (empty = default of the event). */
function QuotaCell({
  pass,
  onSave,
}: {
  pass: iExhibitorPass;
  onSave: (quota: number | null) => void;
}): JSX.Element {
  const [value, setValue] = useState(
    pass.custom_quota === null ? '' : String(pass.custom_quota)
  );
  useEffect(
    () => setValue(pass.custom_quota === null ? '' : String(pass.custom_quota)),
    [pass.custom_quota]
  );
  const full = pass.used >= pass.quota;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography
        sx={{
          fontSize: '13px',
          fontWeight: 600,
          color: full ? '#b45309' : '#111',
          minWidth: 44,
        }}
      >
        {pass.used} / {pass.quota}
      </Typography>
      <TextField
        size="small"
        type="number"
        placeholder="Défaut"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => {
          const next = value === '' ? null : Math.max(0, Number(value));
          if (next !== pass.custom_quota) onSave(next);
        }}
        inputProps={{ min: 0, max: 500, 'aria-label': 'Nombre de badges' }}
        sx={{ width: 90 }}
      />
    </Box>
  );
}

/** Exhibitor codes: each exhibitor registers its staff in one go. */
export default function ExhibitorPasses(): JSX.Element {
  const { eventId = '' } = useParams();
  const navigate = useNavigate();
  const { ticketingRepository } = useApiContext();
  const [data, setData] = useState<iExhibitorPassList | null>(null);
  const [eventName, setEventName] = useState('');
  const [quota, setQuota] = useState('');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{
    type: 'success' | 'error' | 'info' | 'warning';
    text: string;
  } | null>(null);

  const apply = (r: iExhibitorPassList): void => {
    setData(r);
    setQuota(String(r.settings.exhibitor_quota));
  };
  const fail = (e: Error): void =>
    setMessage({ type: 'error', text: e.message });

  useEffect(() => {
    ticketingRepository.getExhibitorPasses(eventId).then(apply).catch(fail);
    ticketingRepository
      .getScanInfo(eventId)
      .then((r) => setEventName(r.event.name))
      .catch(() => setEventName(''));
  }, [eventId]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.passes ?? []).filter(
      (p) =>
        q === '' ||
        `${p.company ?? ''} ${p.contact_email ?? ''} ${p.code}`
          .toLowerCase()
          .includes(q)
    );
  }, [data, search]);

  if (!data)
    return (
      <Box sx={PAGE_SX}>
        {message ? (
          <Alert severity="error">{message.text}</Alert>
        ) : (
          'Chargement…'
        )}
      </Box>
    );

  const notSent = data.passes.filter((p) => !p.sent_at).length;
  const used = data.passes.reduce((n, p) => n + p.used, 0);
  const noType = data.types.length === 0;

  const send = async (ids?: number[]): Promise<void> => {
    setBusy(true);
    try {
      const r = await ticketingRepository.sendExhibitorPasses(eventId, ids);
      setMessage({
        type: r.not_sent.length > 0 ? 'warning' : 'success',
        text:
          `${r.sent} lien(s) envoyé(s).` +
          (r.not_sent.length > 0
            ? ` Sans e-mail valide : ${r.not_sent.join(', ')}.`
            : ''),
      });
      apply(await ticketingRepository.getExhibitorPasses(eventId));
    } catch (e: any) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const copy = (text: string): void => {
    navigator.clipboard
      ?.writeText(text)
      .then(() => setMessage({ type: 'info', text: 'Lien copié.' }))
      .catch(() => undefined);
  };

  return (
    <Box sx={PAGE_SX}>
      <TicketingNav current="exhibitors" canConfigure eventName={eventName} />
      {message && (
        <Alert
          severity={message.type}
          onClose={() => setMessage(null)}
          sx={{ mb: 2 }}
        >
          {message.text}
        </Alert>
      )}

      <Box sx={{ ...card, mb: 3 }}>
        <Typography sx={sectionTitle}>Badges des équipes exposantes</Typography>
        <Typography sx={{ fontSize: '13px', color: '#666', mt: 0.5, mb: 2 }}>
          Chaque exposant reçoit un code et un lien pour inscrire son équipe en
          une seule fois (prénom, nom, fonction, photo facultative), dans la
          limite de son nombre de badges. Il reçoit ensuite tous les badges dans
          un seul PDF.
        </Typography>
        {noType && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Button
                size="small"
                onClick={() =>
                  navigate(`${routes.home2.path}events/${eventId}/ticketing`)
                }
              >
                Configuration
              </Button>
            }
          >
            Créez d&apos;abord un type de billet « Exposant » (Configuration → +
            Exposant).
          </Alert>
        )}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <TextField
            size="small"
            type="number"
            label="Badges par exposant (défaut)"
            value={quota}
            onChange={(e) => setQuota(e.target.value)}
            inputProps={{ min: 0, max: 500 }}
            sx={{ width: 220 }}
          />
          <Select
            size="small"
            displayEmpty
            value={data.settings.exhibitor_type_id ?? ''}
            disabled={noType}
            onChange={(e) => {
              ticketingRepository
                .saveExhibitorPassSettings(eventId, {
                  exhibitor_type_id:
                    e.target.value === '' ? null : Number(e.target.value),
                })
                .then(apply)
                .catch(fail);
            }}
            sx={{ minWidth: 220, fontSize: '13px' }}
          >
            <MenuItem value="">Premier badge exposant actif</MenuItem>
            {data.types.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
                {t.is_active ? '' : ' (inactif)'}
              </MenuItem>
            ))}
          </Select>
          <Button
            variant="contained"
            sx={primaryButton}
            disabled={
              quota === '' || Number(quota) === data.settings.exhibitor_quota
            }
            onClick={() => {
              ticketingRepository
                .saveExhibitorPassSettings(eventId, {
                  exhibitor_quota: Math.max(0, Number(quota)),
                })
                .then((r) => {
                  apply(r);
                  setMessage({
                    type: 'success',
                    text: 'Nombre de badges par défaut enregistré.',
                  });
                })
                .catch(fail);
            }}
          >
            Enregistrer
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          mb: 2,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <TextField
          size="small"
          placeholder="Exposant, e-mail, code"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: '#999' }} />
              </InputAdornment>
            ),
            sx: { borderRadius: '8px', fontSize: '13px', bgcolor: '#fff' },
          }}
          sx={{ minWidth: 260 }}
        />
        <Typography sx={{ fontSize: '13px', color: '#888', flex: 1 }}>
          {data.passes.length} exposant(s) · {used} badge(s) créé(s)
        </Typography>
        <Button
          variant="contained"
          sx={primaryButton}
          disabled={busy || notSent === 0 || noType}
          onClick={() => {
            if (
              window.confirm(
                `Envoyer le lien aux ${notSent} exposant(s) pas encore contactés ?`
              )
            )
              send();
          }}
        >
          Envoyer le lien aux exposants ({notSent})
        </Button>
      </Box>

      {data.passes.length === 0 ? (
        <Box sx={{ ...card, textAlign: 'center', color: '#666' }}>
          Aucun exposant inscrit à ce salon dans EM pour le moment.
        </Box>
      ) : (
        <Box sx={{ ...card, p: 0, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Exposant</TableCell>
                <TableCell>Badges (utilisés / max)</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Lien envoyé</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                      {p.company ?? '—'}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#888' }}>
                      {p.contact_email ?? 'Pas d’e-mail'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <QuotaCell
                      pass={p}
                      onSave={(q) => {
                        ticketingRepository
                          .updateExhibitorPass(eventId, p.id, { quota: q })
                          .then(apply)
                          .catch(fail);
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}
                  >
                    {p.code}
                  </TableCell>
                  <TableCell sx={{ fontSize: '12px', color: '#666' }}>
                    {p.sent_at ? formatDateTime(p.sent_at) : '—'}
                    {p.last_used_at && (
                      <Box sx={{ color: '#166534' }}>
                        Utilisé le {formatDateTime(p.last_used_at)}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Button
                      size="small"
                      sx={{ textTransform: 'none' }}
                      onClick={() => copy(p.url)}
                    >
                      Copier le lien
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ ...secondaryButton, py: 0.25, ml: 1 }}
                      disabled={busy || noType}
                      onClick={() => {
                        void send([p.id]);
                      }}
                    >
                      {p.sent_at ? 'Renvoyer' : 'Envoyer'}
                    </Button>
                    <Button
                      size="small"
                      sx={{ textTransform: 'none', color: '#666', ml: 1 }}
                      onClick={() => {
                        if (
                          window.confirm(
                            'Nouveau code ? L’ancien lien ne fonctionnera plus (les badges déjà créés restent valables).'
                          )
                        )
                          ticketingRepository
                            .regenerateExhibitorPass(eventId, p.id)
                            .then(apply)
                            .catch(fail);
                      }}
                    >
                      Nouveau code
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}
